import { Board } from './board';
import { findMatches, swapMakesMatch, type MatchGroup, type SwapInfo } from './match';
import type { GoalDef, LevelDef } from './level';
import { levelKinds } from './level';
import { Rng } from './rng';
import type { Piece, Pos, Special, TileKind } from './types';
import { TILE_KINDS, isAdjacent, posKey } from './types';

export type Status = 'playing' | 'won' | 'lost';

export interface Move {
  a: Pos;
  b: Pos;
}

export interface Cleared {
  pos: Pos;
  piece: Piece;
}

/**
 * What happened during a turn, in order. The renderer plays these back as
 * animations; the logic never waits for the renderer.
 */
export type Step =
  /** ids[0] is the piece that started at a, ids[1] the one that started at b */
  | { type: 'swap'; a: Pos; b: Pos; ids: [number, number] }
  | { type: 'swapBack'; a: Pos; b: Pos; ids: [number, number] }
  /** colorstar + stripe/bomb: all tiles of one kind turn into specials first */
  | { type: 'transform'; pieces: Cleared[] }
  | {
      type: 'clear';
      /** 1 = the player's own action, 2+ = cascades */
      cascade: number;
      combo: ComboType | null;
      cleared: Cleared[];
      activated: { pos: Pos; piece: Piece; area: Pos[] }[];
      mudHits: { pos: Pos; hpLeft: number }[];
      stainsCleared: Pos[];
      created: Cleared[];
    }
  | { type: 'fall'; moves: { id: number; from: Pos; to: Pos }[]; spawns: Cleared[] }
  | { type: 'shuffle'; moves: { id: number; from: Pos; to: Pos }[]; replaced: Cleared[] }
  | { type: 'end'; status: 'won' | 'lost' };

export type ComboType =
  | 'star+star' | 'star+tile' | 'star+stripe' | 'star+bomb'
  | 'stripe+stripe' | 'stripe+bomb' | 'bomb+bomb';

export interface TurnResult {
  ok: boolean;
  steps: Step[];
}

export interface GoalState {
  def: GoalDef;
  target: number;
  remaining: number;
}

const SCORE = { tile: 10, special: 50, mud: 20, stain: 20 };

/**
 * The complete match-3 rules for one level. No graphics, no timers:
 * every action returns the list of steps it caused. Seeded, so the same
 * seed and the same actions always give the same game (bot + tests).
 */
export class Game {
  readonly board: Board;
  readonly kinds: TileKind[];
  readonly goals: GoalState[];
  movesLeft: number;
  score = 0;
  status: Status = 'playing';
  private readonly rng: Rng;
  private readonly starScores: [number, number];

  /** @param seed ignored when the level fixes its own seed (tutorials) */
  constructor(readonly level: LevelDef, seed = 1) {
    this.rng = new Rng(level.seed ?? seed);
    this.kinds = levelKinds(level);
    this.movesLeft = level.moves;
    this.starScores = level.stars ?? [level.moves * 60, level.moves * 100];
    this.board = this.buildStartBoard();
    this.goals = level.goals.map((def) => {
      const target =
        def.type === 'collect' ? def.count
        : def.type === 'mud' ? this.board.count((c) => !!c.blocker)
        : this.board.count((c) => c.stain);
      return { def, target, remaining: target };
    });
  }

  /** Independent copy (same seed state) – lets the bot try moves without side effects. */
  clone(): Game {
    const g = Object.create(Game.prototype) as Game;
    Object.assign(g, this, {
      board: this.board.clone(),
      rng: this.rng.clone(),
      goals: this.goals.map((x) => ({ ...x })),
    });
    return g;
  }

  // ---------------------------------------------------------------- setup

  private buildStartBoard(): Board {
    // a fully specified layout (tutorials, tests) is used exactly as designed
    if (this.level.board && !this.level.board.some((r) => /(^|\s)~?\.(\s|$)/.test(r))) return Board.parse(this.level.board);
    for (let attempt = 0; attempt < 200; attempt++) {
      const b = this.level.board ? Board.parse(this.level.board) : new Board(8, 8);
      b.fillRandom(this.kinds, this.rng);
      if (findMatches(b).length === 0 && Game.movesOn(b, true).length > 0) return b;
    }
    throw new Error(`level ${this.level.id}: could not create a start board without matches and with a valid move`);
  }

  // ---------------------------------------------------------------- queries

  /** All swaps that do something (for hints, the bot and deadlock detection). */
  validMoves(): Move[] {
    return Game.movesOn(this.board);
  }

  /** Is at least one move possible? (Stops at the first one – much cheaper than validMoves.) */
  hasValidMove(): boolean {
    return Game.movesOn(this.board, true).length > 0;
  }

  private static movesOn(board: Board, firstOnly = false): Move[] {
    const out: Move[] = [];
    for (const a of board.positions()) {
      if (!Game.swappable(board, a)) continue;
      for (const b of [{ row: a.row, col: a.col + 1 }, { row: a.row + 1, col: a.col }]) {
        if (Game.swappable(board, b) && Game.swapDoesSomething(board, a, b)) {
          out.push({ a, b });
          if (firstOnly) return out;
        }
      }
    }
    return out;
  }

  private static swappable(board: Board, p: Pos): boolean {
    return board.isOpen(p) && !!board.at(p).piece;
  }

  private static swapDoesSomething(board: Board, a: Pos, b: Pos): boolean {
    const pa = board.at(a).piece!;
    const pb = board.at(b).piece!;
    if (pa.special === 'colorstar' || pb.special === 'colorstar') return true;
    if (pa.special && pb.special) return true;
    return swapMakesMatch(board, a, b);
  }

  /** 0–3 stars; 1 star for winning, more for score. */
  stars(): number {
    if (this.status !== 'won') return 0;
    return this.score >= this.starScores[1] ? 3 : this.score >= this.starScores[0] ? 2 : 1;
  }

  // ---------------------------------------------------------------- actions

  /** Player swipes piece at `from` onto `to`. Invalid swaps swap back and cost nothing. */
  swap(from: Pos, to: Pos): TurnResult {
    const b = this.board;
    if (this.status !== 'playing' || !isAdjacent(from, to) || !Game.swappable(b, from) || !Game.swappable(b, to)) {
      return { ok: false, steps: [] };
    }
    const ids: [number, number] = [b.at(from).piece!.id, b.at(to).piece!.id];
    const steps: Step[] = [{ type: 'swap', a: from, b: to, ids }];
    this.exchange(from, to);
    const pa = b.at(to).piece!; // the dragged piece, now at `to`
    const pb = b.at(from).piece!;

    const combo = this.comboFor(pa, pb);
    if (combo) {
      this.movesLeft--;
      this.runCombo(combo, pa, pb, to, from, steps);
    } else {
      const groups = findMatches(b, { from, to } satisfies SwapInfo);
      if (groups.length === 0) {
        this.exchange(from, to);
        steps.push({ type: 'swapBack', a: from, b: to, ids });
        return { ok: false, steps };
      }
      this.movesLeft--;
      steps.push(this.wave(1, groups, [], null));
    }
    this.settle(steps);
    return { ok: true, steps };
  }

  /** Booster: wooden hammer hits one cell (tile or mud). No move used. */
  useHammer(p: Pos): TurnResult {
    const cell = this.board.inside(p) ? this.board.at(p) : null;
    if (this.status !== 'playing' || !cell || cell.hole || (!cell.piece && !cell.blocker)) return { ok: false, steps: [] };
    const steps: Step[] = [this.wave(1, [], [p], null)];
    this.settle(steps);
    return { ok: true, steps };
  }

  /** Booster: stirring spoon reshuffles the board. No move used. */
  useShuffle(): TurnResult {
    if (this.status !== 'playing') return { ok: false, steps: [] };
    const steps: Step[] = [this.shuffle()];
    return { ok: true, steps };
  }

  /** +5 moves (purchase, coins or booster). Also continues a lost level. */
  addMoves(n: number): void {
    this.movesLeft += n;
    if (this.status === 'lost' && this.movesLeft > 0) this.status = 'playing';
  }

  // ---------------------------------------------------------------- rules

  private exchange(a: Pos, b: Pos): void {
    const ca = this.board.at(a);
    const cb = this.board.at(b);
    [ca.piece, cb.piece] = [cb.piece, ca.piece];
  }

  private comboFor(a: Piece, b: Piece): ComboType | null {
    const kind = (p: Piece) => (p.special === 'colorstar' ? 'star' : p.special === 'bomb' ? 'bomb' : p.special ? 'stripe' : 'tile');
    const [x, y] = [kind(a), kind(b)].sort((m, n) => ORDER.indexOf(m) - ORDER.indexOf(n));
    if (x === 'star') return `star+${y === 'star' ? 'star' : y}` as ComboType;
    if (y === 'tile' || x === 'tile') return null;
    return `${x}+${y}` as ComboType;
  }

  /** Special + special (or colorstar + anything) swapped together. Centre = where the dragged piece landed. */
  private runCombo(combo: ComboType, dragged: Piece, other: Piece, at: Pos, otherAt: Pos, steps: Step[]): void {
    const b = this.board;
    // both swapped pieces are used up by the combo, except in star+stripe/bomb
    // where the swapped stripe/bomb fires together with its new copies
    const consumed = new Set([dragged.id, other.id]);
    let area: Pos[] = [];
    const nonStar = dragged.special === 'colorstar' ? other : dragged;

    switch (combo) {
      case 'star+star':
        area = b.positions();
        break;
      case 'star+tile':
        area = [at, otherAt, ...this.positionsOfKind(nonStar.kind!)];
        break;
      case 'star+stripe':
      case 'star+bomb': {
        // every tile of that kind becomes the same special, then all fire
        const turned: Cleared[] = [];
        for (const p of this.positionsOfKind(nonStar.kind!)) {
          const piece = b.at(p).piece!;
          if (piece.id === nonStar.id) continue;
          piece.special = combo === 'star+bomb' ? 'bomb' : this.rng.next() < 0.5 ? 'stripeH' : 'stripeV';
          turned.push({ pos: p, piece: { ...piece } });
        }
        steps.push({ type: 'transform', pieces: turned });
        consumed.delete(nonStar.id);
        area = [at, otherAt, ...turned.map((t) => t.pos)];
        break;
      }
      case 'stripe+stripe':
        area = [...this.line(at.row, 'row'), ...this.line(at.col, 'col')];
        break;
      case 'stripe+bomb':
        for (let d = -1; d <= 1; d++) area.push(...this.line(at.row + d, 'row'), ...this.line(at.col + d, 'col'));
        break;
      case 'bomb+bomb':
        area = this.square(at, 2);
        break;
    }
    steps.push(this.wave(1, [], area, combo, consumed));
  }

  private positionsOfKind(kind: TileKind): Pos[] {
    return this.board.positions().filter((p) => this.board.kindAt(p.row, p.col) === kind);
  }

  private line(index: number, dir: 'row' | 'col'): Pos[] {
    const b = this.board;
    if (dir === 'row') return index < 0 || index >= b.rows ? [] : Array.from({ length: b.cols }, (_, col) => ({ row: index, col }));
    return index < 0 || index >= b.cols ? [] : Array.from({ length: b.rows }, (_, row) => ({ row, col: index }));
  }

  private square(c: Pos, r: number): Pos[] {
    const out: Pos[] = [];
    for (let dr = -r; dr <= r; dr++) for (let dc = -r; dc <= r; dc++) {
      const p = { row: c.row + dr, col: c.col + dc };
      if (this.board.inside(p)) out.push(p);
    }
    return out;
  }

  /** Area a special clears when it fires on its own. */
  private areaOf(special: Special, p: Pos): Pos[] {
    switch (special) {
      case 'stripeH': return this.line(p.row, 'row');
      case 'stripeV': return this.line(p.col, 'col');
      case 'bomb': return this.square(p, 1);
      case 'colorstar': {
        // hit by another effect: takes the most common kind on the board
        const counts = new Map<TileKind, number>();
        for (const q of this.board.positions()) {
          const k = this.board.kindAt(q.row, q.col);
          if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
        }
        let best: TileKind | null = null;
        for (const k of TILE_KINDS) if ((counts.get(k) ?? 0) > (best ? counts.get(best)! : 0)) best = k;
        return best ? this.positionsOfKind(best) : [];
      }
    }
  }

  /**
   * One clearing wave: removes matched cells and everything hit by blasts,
   * fires specials in a chain, damages mud, removes stains, then places the
   * specials the matches created.
   * @param consumed pieces used up by a combo – removed without firing again
   */
  private wave(cascade: number, groups: MatchGroup[], blasts: Pos[], combo: ComboType | null, consumed = new Set<number>()): Step & { type: 'clear' } {
    const b = this.board;
    const step: Step & { type: 'clear' } = { type: 'clear', cascade, combo, cleared: [], activated: [], mudHits: [], stainsCleared: [], created: [] };
    const done = new Set<number>();
    const mudHit = new Set<number>();
    const queue: { pos: Pos; blast: boolean }[] = [];

    for (const g of groups) for (const pos of g.cells) queue.push({ pos, blast: false });
    for (const pos of blasts) queue.push({ pos, blast: true });

    const hitMud = (pos: Pos) => {
      const k = posKey(pos);
      const cell = b.at(pos);
      if (!cell.blocker || mudHit.has(k)) return;
      mudHit.add(k);
      cell.blocker.hp--;
      this.score += SCORE.mud;
      step.mudHits.push({ pos, hpLeft: cell.blocker.hp });
      if (cell.blocker.hp <= 0) {
        cell.blocker = null;
        this.progress('mud');
      }
    };

    while (queue.length) {
      const { pos, blast } = queue.shift()!;
      if (!b.inside(pos)) continue;
      const k = posKey(pos);
      const cell = b.at(pos);
      if (cell.blocker) {
        if (blast) hitMud(pos);
        continue;
      }
      if (done.has(k)) continue;
      done.add(k);
      const piece = cell.piece;
      if (!piece) continue;

      if (piece.special && !consumed.has(piece.id)) {
        const area = this.areaOf(piece.special, pos);
        step.activated.push({ pos, piece, area });
        this.score += SCORE.special;
        for (const q of area) queue.push({ pos: q, blast: true });
      }
      cell.piece = null;
      step.cleared.push({ pos, piece });
      this.score += SCORE.tile * cascade;
      if (piece.kind) this.progress('collect', piece.kind);
      if (cell.stain) {
        cell.stain = false;
        step.stainsCleared.push(pos);
        this.score += SCORE.stain;
        this.progress('stain');
      }
    }

    // matches crack the mud next to them
    for (const g of groups) {
      for (const p of g.cells) {
        for (const n of [{ row: p.row - 1, col: p.col }, { row: p.row + 1, col: p.col }, { row: p.row, col: p.col - 1 }, { row: p.row, col: p.col + 1 }]) {
          if (b.inside(n)) hitMud(n);
        }
      }
    }

    for (const g of groups) {
      if (!g.create) continue;
      const cell = b.at(g.create.at);
      if (cell.piece || !b.isOpen(g.create.at)) continue;
      const piece = b.newPiece(g.create.special === 'colorstar' ? null : g.kind, g.create.special);
      cell.piece = piece;
      step.created.push({ pos: g.create.at, piece });
    }
    return step;
  }

  private progress(type: GoalDef['type'], kind?: TileKind): void {
    for (const g of this.goals) {
      if (g.remaining <= 0 || g.def.type !== type) continue;
      if (g.def.type === 'collect' && g.def.kind !== kind) continue;
      g.remaining--;
    }
  }

  /** Gravity, refill and cascades until the board is stable; then win/lose/deadlock checks. */
  private settle(steps: Step[]): void {
    for (let cascade = 2; ; cascade++) {
      steps.push(this.gravity());
      const groups = findMatches(this.board);
      if (groups.length === 0) break;
      steps.push(this.wave(cascade, groups, [], null));
    }
    if (this.goals.every((g) => g.remaining <= 0)) {
      this.status = 'won';
      steps.push({ type: 'end', status: 'won' });
    } else if (this.movesLeft <= 0) {
      this.status = 'lost';
      steps.push({ type: 'end', status: 'lost' });
    } else if (!this.hasValidMove()) {
      steps.push(this.shuffle());
    }
  }

  /**
   * Pieces fall straight down; a cell right below mud or a hole is filled
   * diagonally from a neighbouring column. New pieces enter at the top.
   */
  private gravity(): Step & { type: 'fall' } {
    const b = this.board;
    const start = new Map<number, Pos>();
    for (const p of b.positions()) {
      const piece = b.at(p).piece;
      if (piece) start.set(piece.id, p);
    }
    const spawned = new Set<number>();
    let changed = true;
    while (changed) {
      changed = false;
      for (let r = b.rows - 1; r >= 0; r--) {
        for (let c = 0; c < b.cols; c++) {
          const here = { row: r, col: c };
          if (!b.isOpen(here) || b.at(here).piece) continue;
          const above = { row: r - 1, col: c };
          if (r === 0) {
            const piece = b.newPiece(this.rng.pick(this.kinds));
            b.at(here).piece = piece;
            spawned.add(piece.id);
            changed = true;
          } else if (b.isOpen(above)) {
            if (b.at(above).piece) {
              b.at(here).piece = b.at(above).piece;
              b.at(above).piece = null;
              changed = true;
            }
          } else {
            for (const dc of [-1, 1]) {
              const diag = { row: r - 1, col: c + dc };
              if (b.isOpen(diag) && b.at(diag).piece) {
                b.at(here).piece = b.at(diag).piece;
                b.at(diag).piece = null;
                changed = true;
                break;
              }
            }
          }
        }
      }
    }
    const step: Step & { type: 'fall' } = { type: 'fall', moves: [], spawns: [] };
    for (const p of b.positions()) {
      const piece = b.at(p).piece;
      if (!piece) continue;
      if (spawned.has(piece.id)) step.spawns.push({ pos: p, piece });
      else {
        const from = start.get(piece.id)!;
        if (from.row !== p.row || from.col !== p.col) step.moves.push({ id: piece.id, from, to: p });
      }
    }
    return step;
  }

  /**
   * Rearranges the pieces (specials keep their kind) until there is no match
   * and at least one valid move. Falls back to new random tiles.
   */
  private shuffle(): Step & { type: 'shuffle' } {
    const b = this.board;
    const cells = b.positions().filter((p) => b.isOpen(p) && b.at(p).piece);
    const original = new Map(cells.map((p) => [b.at(p).piece!.id, p]));
    const pieces = cells.map((p) => b.at(p).piece!);
    const step: Step & { type: 'shuffle' } = { type: 'shuffle', moves: [], replaced: [] };

    let ok = false;
    for (let attempt = 0; attempt < 100 && !ok; attempt++) {
      this.rng.shuffle(pieces);
      cells.forEach((p, i) => (b.at(p).piece = pieces[i]));
      ok = findMatches(b).length === 0 && this.hasValidMove();
    }
    for (let attempt = 0; attempt < 100 && !ok; attempt++) {
      // too few kinds to rearrange: replace plain tiles with new random ones
      for (const p of cells) if (!b.at(p).piece!.special) b.at(p).piece = null;
      b.fillRandom(this.kinds, this.rng);
      ok = findMatches(b).length === 0 && this.hasValidMove();
    }
    for (const p of cells) {
      const piece = b.at(p).piece!;
      const from = original.get(piece.id);
      if (!from) step.replaced.push({ pos: p, piece });
      else if (from.row !== p.row || from.col !== p.col) step.moves.push({ id: piece.id, from, to: p });
    }
    return step;
  }
}

const ORDER = ['star', 'stripe', 'bomb', 'tile'];
